import {all, call, cancel, cancelled, delay, fork, put, select, take, takeLatest} from 'redux-saga/effects'
import {get, pick} from 'lodash'
import Lock from '../helpers/lock'
import {getFailureCode, getFailureReason} from 'helpers/api'
import {generalError} from '../actions'
import * as actions from '../actions/state'
import * as uiActions from '../actions/ui'
import {snackbarMessage} from 'actions/snackbar'
import types from '../actions/types'
import {connect} from 'actions/socket'
import getSaveType, {PersistenceType} from './persistance/eventWhitelist'
import stateWhitelist from './persistance/stateWhitelist'
import {createAPI} from 'services/api'
import {getRegionalLocale} from 'selectors/settings'
import {getGlobalFeatureToggle, getUser} from 'selectors/auth'
import {getSessionId} from 'selectors/data'
import {initialiseTracking} from 'helpers/tracking'
import {featureToggles} from 'helpers/constants'
import {t} from "helpers/language"
import {notificationsLoad} from 'actions/notifications'

const DEBOUNCE_TIME = 3000
const UI_READY_TIME = 500

export default (api) => {

  function* sessionLoggedIn() {

    try {
      // We are now authenticated, hydrate the state and other required things
      const response = yield call(api.state.load)
      if (response.ok) {
        const persistedState = response.data
        yield put(actions.loadStateSuccess(persistedState))

        yield call(loadFeatureToggles)
        yield delay(UI_READY_TIME)
        yield put(uiActions.uiReady())

        yield put(notificationsLoad())

        const sessionId = yield select(getSessionId)
        yield put(connect(sessionId))

        // Hydrate the global locale for moment
        const moment = require('moment')
        const locale = yield select(state => getRegionalLocale(state))
        moment.locale(locale)

      } else {
        yield put(actions.loadStateFailure(getFailureReason(response), getFailureCode(response)))
        yield put(uiActions.uiReady())
      }
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
      yield put(uiActions.uiReady())
    }
  }

  function* sessionNotLoggedIn() {
    try {
      // We are not authenticated, load the global feature toggles and mark UI is ready
      yield call(loadFeatureToggles)
      yield delay(UI_READY_TIME)
      yield put(uiActions.uiReady())
    } catch (e) {
      yield put(generalError(e.message))
      yield put(uiActions.uiReady())
    }
  }

  // This may be called with or without a valid user state
  function* loadFeatureToggles() {
    const user = yield select(getUser)
    const accountId = get(user, 'accountId')

    const { featureTogglesUri, systemName } = yield select(state => state.data)

    const api = createAPI(featureTogglesUri)
    const response = yield call(api.get, '/rest/features', { system: systemName, accountId })
    if (response.ok) {
      yield put(actions.setFeatureToggles(response.data.result))
      yield call(initialiseTrackingIfEnabled)
    } else {
      console.error("Failed to load feature toggles", response)
      yield put(snackbarMessage(t("Sorry something went wrong, some features may not be available")))
    }
  }

  function* initialiseTrackingIfEnabled() {
    const isHubspotTrackingEnabled = yield select(state => getGlobalFeatureToggle(state, featureToggles.HUBSPOT_TRACKING))
    initialiseTracking(isHubspotTrackingEnabled)
  }

  //Saves the state to the server
  function* save(action) {
    yield put(actions.saveState(action))
    const unfilteredState = yield select()
    const state = pick(unfilteredState, stateWhitelist(unfilteredState))
    try {
      const result = yield call(api.state.save, state, action)
      if (result.ok) {
        yield put(actions.saveStateSuccess())
      } else {
        yield put(actions.saveStateFailure(0, 0))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  //Ensures that the state is only persisted after DEBOUNCE_TIME ms of no changes.
  function* debounceSave() {
    try {
      yield delay(DEBOUNCE_TIME)
      yield call(save)
    } catch (e) {
      console.error(e)
    }
  }

  // Triggered after the state is loaded - when whitelisted actions occur the state will be saved
  function* statePersister() {
    try {
      const debounceLock = new Lock(debounceSave)
      while (true) {
        const action = yield take()
        const type = getSaveType(action)
        if (type) {
          // each persistence action cancels the debounce timer
          yield debounceLock.cancel()

          //if immediate task do now else debounce
          if (type === PersistenceType.IMMEDIATE) {
            yield fork(save, action)
          } else if (type === PersistenceType.DEBOUNCE) {
            // create a new debounce timer
            yield debounceLock.execute(action)
          }

          if (yield cancelled()) {
            // we can do some work on log out - any further requests will fail so cancel
          }
        }
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* watcher() {
    yield all([
      takeLatest(types.LOGIN_SUCCESS, sessionLoggedIn),
      takeLatest(types.SESSION_LOGIN_FAILURE, sessionNotLoggedIn),
    ])
  }

  function* stateWatcher() {
    while (yield take(types.STATE_LOAD_SUCCESS)) {
      // start the background session check
      const persister = yield fork(statePersister)
      // wait for log out
      yield take(types.LOGOUT_ATTEMPT)
      // and cancel the job
      yield cancel(persister)
    }
  }

  return {
    watcher,
    stateWatcher,
  }
}
